<template>
    <div class="container">
        <h1>Les portefeuilles compatibles avec le staking d'ADA</h1>

        <p class="intro">
            Vous voulez garder précieusement vos ADAs en attendant qu'ils prennent de la valeur, mais ça ne serait pas une mauvaise idée du tout de gagner 4% à 8% d'intérêts par an au passage ! Comme il n'est pas recommandé de laisser traîner vos
            fonds sur des exchanges, et qu'aucun d'entre eux ne propose ce niveau d'intérêts annuels, voici une liste des portefeuilles qui sont compatible avec le <a href="/doc-cardano/qu-est-ce-que-le-staking">staking d'ADA</a>.
        </p>

        <h2>Les portefeuilles HOT et COLD</h2>
        <p>Avant de rentrer dans le listing des portefeuilles, il faut comprendre la différence fondamentale en terme de sécurité entre les portefeuilles dits <mark>HOT</mark> et ceux dits <mark>COLD</mark>.</p>

        <p>Vos portefeuilles opèrent des <mark>adresses</mark> sur lesquelles <b>vous avez le contrôle</b>, mais on va rentrer en détail de ce qui vous donne précisement le contrôle de ceux-ci.</p>

        <h3>Le contrôle du portefeuille</h3>
        <p>
            Les cryptomonnaies sont toutes basées sur de la <a href="https://fr.wikipedia.org/wiki/Cryptographie_asym%C3%A9trique">cryptographie asymétrique</a>, un mécanisme permettant de générer
            <b>2 clés, l'une partagée (publique) et l'autre secrète (privée)</b>. Sans aller dans les détails théoriques, voici ce que nous garantit ce procédé cryptographique:
        </p>
        <ul>
            <li>Il est <i>impossible</i> de déduire la clé privée de la clé publique</li>
            <li>Les messages chiffrés par la clé publique ne sont déchiffrables qu'à l'aide de la clé privée</li>
            <li>Les messages chiffres par la clé privée ne sont déchiffrables qu'à l'aide de la clé publique</li>
        </ul>

        <p>
            On peut donc n'envoyer des messages <b>lisibles que par vous</b> en les encodant avec la <b>clé publique</b> que vous partagez, et on est certain que les messages qui sont décodables avec la clé publique <b>proviennent de vous</b>, car
            vous les avez chiffré avec la clé privée. On dit que vous les avez <mark>signés</mark>.
        </p>

        <p>C'est donc bel et bien <b>la clé privée qui contrôle le portefeuille</b>. Si quelqu'un y accède, il pourra vous voler vos fonds. Maintenant que c'est compris, voyons ce qui différencie les portefeuilles HOT et COLD.</p>

        <h3>Les portefeuilles HOT: software wallets</h3>
        <p>
            Les portefeuilles HOT sont des logiciels. Ils stockent la clé privée sur le périphérique sur lequel ils sont installés. Même si la grande majorité d'entre eux vont chiffrer votre clé privée avec votre mot de passe (que
            <a href="https://fr.wikipedia.org/wiki/Robustesse_d%27un_mot_de_passe#Recommandations_pour_des_mots_de_passe_robustes_pour_les_mots_de_passe_forts">vous devriez choisir fort</a>), leur niveau de sécurité dépend presque directement du
            niveau de sécurité de ce périphérique. <b>Choisir un mot de passe fort n'a d'intérêt que si un malware n'est pas en train d'enregistrer ce que vous tapez au clavier.</b>
        </p>

        <p>
            Quelqu'un qui utilise un HOT wallet devrait donc être attentif aux critères suivants:
        </p>
        <ul>
            <li>protection contre les virus</li>
            <li>protection pare-feu</li>
            <li>
                encryption du disque-dur, ou même installation du wallet sur un
                <a href="https://www.google.com/search?q=secure+drive&source=lmns&tbm=shop">secure-drive</a>
            </li>
            <li>protection de l'accès physique au périphérique (mot de passe de session, code PIN, verouillage rapide en cas d'inactivité, etc)</li>
            <li>politique de sécurité quand à l'installation de logiciels et ressources externes</li>
            <li>politique de mise à jour automatique et régulière</li>
        </ul>

        <h3>Les portefeuilles COLD: hardware wallets</h3>
        <p>
            Les portefeuilles COLD sont des dispositifs hardware qui se connectent (en général par USB, USB-C, micro-USB) à votre machine (PC, laptop, smartphone) pour échanger des données avec les logiciels installés. L'avantage en terme de sécurité
            est que <b>la clé privée ne quitte jamais le COLD wallet</b>, elle n'ira ni sur votre machine, ni sur Internet. Toute opération nécessitant une signature sera envoyée au wallet et effectuée localement dessus.
        </p>

        <h2>Les portefeuilles compatibles avec le Staking</h2>

        <div class="alert alert-warning">
            Nous présentons ici les différents modèles compatibles afin de guider le lecteur. Nous ne mettons ici aucun lien d'affilitation et nous n'avons aucun partenariat avec les différents portefeuilles présentés.
        </div>

        <p>Maintenant qu'on a revu un peu la théorie, passons à la pratique, et voyons ensemble quels sont les portefeuilles disponible pour notre opération</p>

        <h3>Les portefeuilles HOT compatibles avec le staking d'ADA</h3>
        <p>
            Il existe 3 principaux portefeuilles HOT compatible avec le Staking:
        </p>
        <ul>
            <li><b>Daedalus</b>: Un portefeuille <b>full-node</b> et open-source développé par <a href="https://iohk.io/">IOHK</a></li>
            <li><b>Yoroi</b>: Un portefeuille <b>light</b> developpé par <a href="https://emurgo.io/">Emurgo</a>, la branche commerciale officielle du projet Cardano</li>
            <li><b>Adalite</b>: Un portefeuille <b>light</b> et open-source développé par <a href="https://www.vacuumlabs.com/vacuumlabs-we-put-the-tech-in-fintech">VacuumLabs</a></li>
        </ul>

        <p>Prenons un peu de temps pour parler des différences entre ces 3 wallets.</p>

        <h4 id="daedalus">Daedalus, le portefeuille officiel de Cardano</h4>
        <p>
            IOHK, la société contractée pour le développement de la blockchain Cardano, fournit un portefeuille open-source et full-node. Par <b>full-node</b>, on entend qu'il réalise lui-même les opérations sur la blockchain Cardano. Il synchronise
            localement la blockchain (comptez 5Go au minimum), et ne nécessite de faire confiance à aucune autre entité pour fonctionner.
        </p>

        <p>
            C'est donc un logiciel qui fait son poids, mais il est open-source et fonctionne bien sans devoir faire confiance à personne.
        </p>

        <h4 id="yoroi">Yoroi, le portefeuille light</h4>
        <p>
            Yoroi, développé par Emurgo, est un portefeuille <b>light</b>. Il n'intègre pas une copie de la blockchain localement, mais envoit les requètes à des serveurs de confiance. S'il n'est pas totalement <b>trustless</b>, il dispose du gros
            avantage d'être connectable à des <b>cold wallets</b>.
        </p>

        <h4 id="adalite">Adalite, le portefeuille light et open-source</h4>
        <p>
            Adalite est un concurrent direct de Yoroi. Il est développé par une tiers du nom de VacuumLabs. Comme Yoroi, il intègre la possibilité de connecter des COLD wallets. S'il dispose de moins de fonctionnalité que Yoroi, il est en revanche
            <a href="https://github.com/vacuumlabs/adalite">entièrement open-source</a>.
        </p>

        <h3>Les portefeuilles COLD compatibles avec le staking d'ADA</h3>

        <p>A ce stade, il n'existe aucun portefeuille COLD qui puisse intégrer <b>directement</b> le staking d'ADA, étant donné le faible espace de stockage disponible et la complexité de la tâche.</p>

        <p>
            Mais, (vous vous doutez bien qu'il y a un "mais"), il existe 3 portefeuilles COLD qui sont compatibles avec Yoroi et Adalite ! Et quand on connecte un portefeuille <b>COLD</b> avec un portefeuille <b>HOT</b>, ça donne un portefeuille...
            <b>COLD</b> ! Et oui, oubliez les jeux idiots de terminologie, vos clés privées resteront dans votre portefeuille COLD et ne bougeront pas de là.
        </p>

        <p>
            Les 3 portefeuilles utilisables ici sont aussi (non pas par hasard) les 3 plus répandus.
        </p>

        <div class="row">
            <div class="col12 col-lg-4">
                <h4>Ledger Nano S</h4>

                <img class="mx-auto d-block" v-lazy-load data-src="/img/doc/ledger-nano-s.webp" alt="Portfeuille compatible ADA-staking: ledger nano s" style="max-height: 250px;" />
                <p>Disponible aux alentours de <b>60€</b></p>
                <p>
                    Le plus vendu de tous les COLD wallet, le Ledger Nano S permet de gérer jusqu'à 3 comptes de cryptomonnaies différents.
                </p>
            </div>
            <div class="col12 col-lg-4">
                <h4>Ledger Nano X</h4>

                <img class="mx-auto d-block" v-lazy-load data-src="/img/doc/ledger-nano-x.webp" alt="Portfeuille compatible ADA-staking: ledger nano s" style="max-height: 250px;" />
                <p>Disponible aux alentours de <b>120€</b></p>
                <p>
                    La version premium du Ledger Nano S, le Nano X permet de stocker jusqu'à une centaine d'applications (et donc de cryptomonnaies).
                </p>
            </div>
            <div class="col12 col-lg-4">
                <h4>Trezor Wallet model T</h4>

                <img class="mx-auto d-block" v-lazy-load data-src="/img/doc/trezor-wallet.webp" alt="Portfeuille compatible ADA-staking: ledger nano s" style="max-height: 250px;" />
                <p>Disponible aux alentours de <b>180€</b></p>
                <p>
                    Le Trezor Wallet, principal concurrent des Ledger, permet de stocker jusqu'à 1000 crypto-monnaies différentes. Doté d'un touch-screen.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title: "Les portefeuilles compatibles avec le staking d'ADA - Stakepool.fr",
        description: "Les différents portefeuilles COLD ou HOT compatibles avec ADA, en détails.",
    }),
};
</script>
